/**
 * LiveZone - Dynamic sports content zone that displays real-time content.
 * Supports multiple positions: top, sidebar, bottom, overlay, embedded
 * Content types are registered via LiveZoneRegistry.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import LiveZoneRegistry from './LiveZoneRegistry.mjs';

// Fallback placeholder for unregistered content types
const PlaceholderContent = ({ type }) => (
    <div className="live-zone__placeholder">
        <span className="live-zone__placeholder-text">
            {type} content loading...
        </span>
    </div>
);

/**
 * LiveZone component - Renders configured live content types
 * @param {string} position - Which position this LiveZone is rendering for
 * @param {string} className - Additional CSS classes
 */
const LiveZone = ({ position = 'top', className = '' }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Get Live Zone preferences from Redux
    const liveZonePrefs = useSelector(state =>
        state.pageLayoutSettings?.userLiveZonePreferences || {}
    );

    // Check if Live Zone is enabled
    const enabled = liveZonePrefs.enabled !== false;

    // Determine if this position should render
    const configuredPosition = liveZonePrefs.position || 'top';

    // Get content types based on mobile/desktop
    const desktopContentTypes = liveZonePrefs.contentTypes || ['scores'];
    const mobileOverrides = liveZonePrefs.mobileOverrides || {};
    const mobileContentTypes = mobileOverrides.contentTypes || ['scores'];

    // Detect mobile on mount
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Set initial collapsed state from mobile override
    useEffect(() => {
        if (isMobile && mobileOverrides.collapsed !== undefined) {
            setIsCollapsed(mobileOverrides.collapsed);
        }
    }, [isMobile, mobileOverrides.collapsed]);

    // Don't render if disabled
    if (!enabled) return null;

    // On mobile, handle position override
    const effectivePosition = isMobile && mobileOverrides.position
        ? mobileOverrides.position
        : configuredPosition;

    // Don't render if this isn't the configured position
    if (effectivePosition !== position) return null;

    // Get content types based on device
    const contentTypes = isMobile ? mobileContentTypes : desktopContentTypes;

    const handleToggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <div
            className={`live-zone live-zone--${position} ${isCollapsed ? 'live-zone--collapsed' : ''} ${className}`}
            data-live-zone-position={position}
            data-collapsed={isCollapsed}
        >
            {/* Mobile collapse toggle */}
            {isMobile && (
                <button
                    className="live-zone__toggle"
                    onClick={handleToggleCollapse}
                    aria-expanded={!isCollapsed}
                    aria-label={isCollapsed ? 'Expand Live Zone' : 'Collapse Live Zone'}
                >
                    <span className="live-zone__toggle-icon">
                        {isCollapsed ? '▼' : '▲'}
                    </span>
                    <span className="live-zone__toggle-text">
                        {isCollapsed ? 'Show Scores' : 'Hide'}
                    </span>
                </button>
            )}

            {/* Content area */}
            <div className="live-zone__content">
                {contentTypes.map(type => {
                    const ContentComponent = LiveZoneRegistry.get(type);
                    return ContentComponent ? (
                        <ContentComponent
                            key={type}
                            refreshInterval={liveZonePrefs.autoRefreshInterval || 30}
                        />
                    ) : (
                        <PlaceholderContent key={type} type={type} />
                    );
                })}
            </div>
        </div>
    );
};

export default LiveZone;
